import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRightLeft, BookText, Coins, FileSpreadsheet, LayoutDashboard, LogOut,
  Menu, ReceiptIndianRupee, Scale, TrendingUp, Gem, ChevronRight,
} from 'lucide-react';

import AccountsHome from './AccountsHome';
import ChartOfAccountsScreen from './ChartOfAccountsScreen';
import TransactionJournalScreen from './TransactionJournalScreen';
import GSTSetup from '../Accounts/GSTSetup';
import TrialBalance from '../Accounts/TrialBalance';
import ProfitLoss from '../Accounts/ProfitLoss';
import BalanceSheet from '../Accounts/BalanceSheet';
import PartyLedger from '../Accounts/PartyLedger';
import OutstandingReport from '../Accounts/OutstandingReport';
import MetalExchangeList from '../Accounts/MetalExchange/MetalExchangeList';
import SilverIntake from '../Accounts/MetalExchange/SilverIntake';
import SilverStockLedger from '../Accounts/MetalExchange/SilverStockLedger';
import MetalExchangeReports from '../Accounts/MetalExchange/MetalExchangeReports';
import PayrollApproval from '../Accounts/PayrollApproval';
import { getStoredUser, subscribeApiActivity } from '../api';
import ModuleGoalsDashboard from '../Goals/ModuleGoalsDashboard';

const ACC_STYLES = `
  .acc-layout { display: flex; height: 100vh; overflow: hidden; font-family: 'Inter', sans-serif; }
  .acc-sidebar {
    width: 260px; min-width: 260px; background: linear-gradient(180deg,#1e1b4b 0%,#312e81 50%,#1e293b 100%);
    color: white; display: flex; flex-direction: column; transition: transform .3s ease; z-index: 20;
    box-shadow: 4px 0 24px rgba(0,0,0,.3);
  }
  .acc-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: #f8fafc; }
  .acc-header {
    height: 64px; background: white; border-bottom: 1px solid #e2e8f0;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 24px; box-shadow: 0 1px 4px rgba(0,0,0,.06); z-index: 10; flex-shrink: 0;
  }
  .acc-content { position: relative; flex: 1; overflow-y: auto; padding: 24px; background: #f8fafc; }
  .acc-content,
  .acc-content h1, .acc-content h2, .acc-content h3, .acc-content h4, .acc-content h5, .acc-content h6,
  .acc-content p, .acc-content span, .acc-content label, .acc-content small,
  .acc-content td, .acc-content th, .acc-content li, .acc-content a,
  .acc-content input, .acc-content textarea, .acc-content select {
    color: #000 !important;
  }
  .acc-content input::placeholder,
  .acc-content textarea::placeholder { color: #000 !important; opacity: 1; }
  .acc-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 19; display: none; }
  .acc-overlay.visible { display: block; }
  .acc-nav-scroll { padding: 16px 12px; display: flex; flex-direction: column; gap: 4px; flex: 1; overflow-y: auto; }
  .acc-nav-label { font-size: 10px; font-weight: 700; color: #6366f1; text-transform: uppercase; letter-spacing: 1px; padding: 0 4px 8px; }
  .acc-nav-item {
    display: flex; align-items: center; gap: 12px; padding: 10px 16px; border-radius: 10px;
    cursor: pointer; transition: all .2s; border: none; background: transparent; color: #a5b4fc;
    font-size: 14px; font-weight: 500; width: 100%; text-align: left;
  }
  .acc-nav-item:hover { background: rgba(255,255,255,.08); color: white; }
  .acc-nav-item.active { background: rgba(255,255,255,.12); color: white; box-shadow: inset 0 0 0 1px rgba(255,255,255,.1); }
  .acc-busy {
    pointer-events: auto; position: absolute; inset: 0; z-index: 30; display: flex;
    align-items: flex-start; justify-content: center; background: rgba(255,255,255,.55); backdrop-filter: blur(1px);
  }
  .acc-busy-card {
    margin-top: 24px; display: inline-flex; align-items: center; gap: 12px; border-radius: 16px;
    border: 1px solid #e2e8f0; background: white; padding: 12px 18px; box-shadow: 0 12px 28px rgba(15,23,42,.12);
  }
  .acc-spinner {
    width: 18px; height: 18px; border-radius: 999px; border: 2px solid #e0e7ff;
    border-top-color: #4f46e5; animation: acc-spin .7s linear infinite;
  }
  @keyframes acc-spin { to { transform: rotate(360deg); } }
  @media(max-width:768px) {
    .acc-sidebar { position: fixed; height: 100%; transform: translateX(-100%); }
    .acc-sidebar.open { transform: translateX(0); }
    .acc-content { padding: 16px; }
  }
  @media(min-width:769px) { .acc-sidebar { position: relative; transform: none !important; } }
`;

const NAV_GROUPS = [
  {
    id: 'core-finance',
    label: 'Core Finance',
    items: [
      { id: 'overview', label: 'Overview', icon: LayoutDashboard },
      { id: 'coa', label: 'Chart of Accounts', icon: BookText },
      { id: 'transactions', label: 'Daily Transactions', icon: ArrowRightLeft },
      { id: 'gst-invoices', label: 'GST & Invoices', icon: ReceiptIndianRupee },
      { id: 'party-ledger', label: 'Party Ledger', icon: BookText },
      { id: 'outstanding-report', label: 'Outstanding Report', icon: ArrowRightLeft },
      { id: 'payroll-approval', label: 'Payroll Approval', icon: ReceiptIndianRupee },
      { id: 'trial-balance', label: 'Trial Balance', icon: FileSpreadsheet },
      { id: 'profit-loss', label: 'Profit & Loss', icon: TrendingUp },
      { id: 'balance-sheet', label: 'Balance Sheet', icon: Scale },
      { id: 'goals', label: 'Goals & Tasks', icon: TrendingUp },
    ],
  },
  {
    id: 'stock-management',
    label: 'Metal Exchange',
    items: [
      { id: 'metal-exchange', label: 'Metal Exchange', icon: Coins },
      { id: 'silver-intake', label: 'Silver Intake', icon: Coins },
      { id: 'silver-stock', label: 'Silver Stock Ledger', icon: Scale },
      { id: 'metal-exchange-reports', label: 'Exchange Reports', icon: FileSpreadsheet },
    ],
  },
];

const SCREEN_MAP = {
  overview: AccountsHome,
  coa: ChartOfAccountsScreen,
  transactions: TransactionJournalScreen,
  'trial-balance': TrialBalance,
  'profit-loss': ProfitLoss,
  'balance-sheet': BalanceSheet,
  'gst-invoices': GSTSetup,
  'silver-intake': SilverIntake,
  'silver-stock': SilverStockLedger,
  'metal-exchange': MetalExchangeList,
  'metal-exchange-reports': MetalExchangeReports,
  'party-ledger': PartyLedger,
  'outstanding-report': OutstandingReport,
  'payroll-approval': PayrollApproval,
  goals: () => <ModuleGoalsDashboard moduleName="ACCOUNTS" />,
};

function asText(value) {
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (value && typeof value === 'object') {
    return String(
      value.display_name ||
      value.name ||
      value.username ||
      value.role ||
      value.user_role ||
      value.message ||
      value.code ||
      ''
    ).trim();
  }
  return '';
}

function AccountantWorkspace({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [apiBusy, setApiBusy] = useState(false);

  const ActiveScreen = useMemo(() => SCREEN_MAP[activeTab] || AccountsHome, [activeTab]);
  const activeItem = NAV_GROUPS.flatMap((group) => group.items).find((item) => item.id === activeTab) || NAV_GROUPS[0].items[0];
  const getPageTitle = () => activeItem.label;
  const storedUser = getStoredUser();
  const displayName = [
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim(),
    [storedUser?.first_name, storedUser?.last_name].filter(Boolean).join(' ').trim(),
    asText(user?.display_name),
    asText(storedUser?.display_name),
    asText(user?.name),
    asText(storedUser?.name),
    asText(user?.username),
    asText(storedUser?.username),
  ].find((value) => asText(value)) || 'Accounts User';
  const displayRole = [
    asText(user?.role),
    asText(user?.user_role),
    asText(storedUser?.role),
    asText(storedUser?.user_role),
  ].find((value) => asText(value)) || 'Finance Workspace';

  useEffect(() => subscribeApiActivity((count) => setApiBusy(count > 0)), []);

  return (
    <>
      <style>{ACC_STYLES}</style>
      <div className="acc-layout prod-module-root">
        <div className={`acc-overlay ${sidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)} />

        <aside className={`acc-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg,#f59e0b,#d97706)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Gem size={22} color="white" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: '-.5px', color: 'white' }}>
                ZILVERRA<span style={{ color: '#818cf8' }}>JEWELS</span>
              </div>
              <div style={{ fontSize: 11, color: '#a5b4fc' }}>Accounts Module</div>
            </div>
          </div>

          <div className="acc-nav-scroll">
            {NAV_GROUPS.map((group) => (
              <div key={group.id}>
                <div className="acc-nav-label">{group.label}</div>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.id === activeTab;
                  return (
                    <button
                      key={item.id}
                      className={`acc-nav-item ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab(item.id);
                        setSidebarOpen(false);
                      }}
                    >
                      <Icon size={18} />
                      <span>{item.label}</span>
                      {isActive && <ChevronRight size={14} style={{ marginLeft: 'auto' }} />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div style={{ margin: '12px', padding: '12px', background: 'rgba(0,0,0,.25)', borderRadius: 10, border: '1px solid rgba(255,255,255,.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>
                {String(displayName || 'A').charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>{displayName}</div>
                <div style={{ fontSize: 11, color: '#a5b4fc' }}>{displayRole}</div>
              </div>
            </div>
          </div>
        </aside>

        <main className="acc-main">
          <header className="acc-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1a1a1a', padding: 4, display: 'flex', alignItems: 'center' }}
                onClick={() => setSidebarOpen((prev) => !prev)}
              >
                <Menu size={22} />
              </button>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#000', margin: 0 }}>{getPageTitle()}</h2>
            </div>
            <button
              onClick={() => window.confirm('Logout?') && onLogout?.()}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              <LogOut size={16} />
              Logout
            </button>
          </header>

          <div className="acc-content">
            {apiBusy && (
              <div className="acc-busy">
                <div className="acc-busy-card">
                  <span className="acc-spinner" aria-hidden="true" />
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>Loading account data...</div>
                </div>
              </div>
            )}
            <ActiveScreen />
          </div>
        </main>
      </div>
    </>
  );
}

export default AccountantWorkspace;
